import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle } from '@fortawesome/pro-light-svg-icons/faCheckCircle';

import classNames from 'clsx';

import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import { Text, TextType } from 'ui-components/components/text';

import { FormattedCurrency } from '../../../components/formatted-currency';
import messages from './messages';

import style from './recommendation-banner-content.module.less';

/**
 * @typedef {import('../../../prop-types/currency')} Currency
 */

/**
 * @param {object} props
 * @param {Currency} props.appliedSavings - The amount of savings that have been applied from this recommendation
 * @param {React.ReactNode} props.applyButton - The button to apply the recommendation.
 * @param {React.ReactNode} props.bannerText - The text to display in the banner.
 * @param {string} [props.className] - The className to apply to the banner.
 * @param {boolean} props.isApplyingSavings - Whether savings are being applied.
 * @param {'light'|'medium'|'dark'} [props.theme] - The theme of the banner, either 'light', 'medium', or 'dark'.
 * @param {string} props.loadingIndicatorContainerClassName
 * @returns {React.JSX.Element}
 */
export const RecommendationBannerContent = ({
  appliedSavings,
  applyButton,
  bannerText,
  className,
  isApplyingSavings = false,
  theme = 'light',
  loadingIndicatorContainerClassName,
}) => {
  if (isApplyingSavings) {
    return (
      <div className={classNames(style['recommendation-loading-indicator-container'], loadingIndicatorContainerClassName)}>
        <LoadingIndicator
          className={style['recommendation-loading-indicator']}
          iconClassName={style['recommendation-loading-indicator-icon']}
          theme={theme === 'light' ? 'grey' : 'white'}
        />
        <Text textType={TextType.Paragraph} value={messages.applyingSavings} />
      </div>
    );
  }

  if (appliedSavings?.value) {
    return (
      <div className={style['recommendation-applied-banner-content']}>
        <Text
          className={style['recommendation-applied-text']}
          formattedMessageValues={{
            savingsAmount: (
              <strong>
                <FormattedCurrency currencyCode={appliedSavings.code} value={appliedSavings.value} />
              </strong>
            ),
          }}
          textType={TextType.Paragraph}
          value={messages.savingsApplied}
        />
        <FontAwesomeIcon
          className={classNames(
            style['recommendation-applied-checkmark'],
            { [style['recommendation-applied-checkmark-dark']]: theme === 'dark' },
            { [style['recommendation-applied-checkmark-medium']]: theme === 'medium' },
            { [style['recommendation-applied-checkmark-light']]: theme === 'light' },
          )}
          icon={faCheckCircle}
        />
      </div>
    );
  }

  return (
    <div className={classNames(style['recommendation-banner-content'], className)}>
      <div className={style['recommendation-text-wrapper']}>{bannerText}</div>
      {applyButton}
    </div>
  );
};
